import {
  ClipboardEvent,
  DragEvent,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  Ref,
} from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Tooltip } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import LoadingIcon from "@/components/DotPulse";
import cls from "classnames";
import { Input } from "antd";
import useMessage from "logic/src/utils/useMessage";
import Vm, { EValidType } from "logic/src/businesses/fileUpload/vm";
import styles from "./index.module.scss";
import { ReactComponent as FileUploadIcon } from "@/components/icons/fileUpload.svg";
import { ReactComponent as UploadImageIcon } from "logic/src/components/icons/image.svg";
import { ReactComponent as SendMsgIcon } from "logic/src/components/icons/sendMsg.svg";
import { IOptions as IUploadOptions } from "logic/src/objects/file/NovaFileManagerModel";
import FileDisplay from "@/components/FileDisplay";
import {
  EUploadStatus,
  UploadedFile,
  TRichModalFileStatus,
  ERichModalFileNormStatus,
  ERichModalFileParseStatus,
  IFile,
} from "logic/src/objects/file/types";
import { IRichModalFile } from "logic/src/objects/file/types";
import RefText from "logic/src/components/RefText";
import { EFileQueryType, ISelection } from "logic/src/objects/chat/types";
import SessionFile from "logic/src/objects/session/sessionFile";
import { sendCLK } from "logic/src/utils/eventTrack";

// import { isImageFileName } from "@/utils/upload";
import {
  isFileNameAccept,
  pureSelectFiles,
  parseStatus,
  getFileStatusText,
  isImageFileName,
  isImage,
} from "logic/src/objects/file/utils/index";
import {
  MAX_UPLOAD_FILE_NUMBER,
  MAX_UPLOAD_FILE_SIZE,
  MAX_UPLOAD_IMAGE_NUMBER,
} from "logic/src/objects/file/constants";
import { IShortCommand } from "@/pages/VideoUnderstanding/types";

const IMAGE_FILE_INVALID_TEXT = "暂不支持同时上传文档和图片";
const IMAGE_MAX_NUM_TEXT = "最多上传4个图片";
const FILE_MAX_NUM_TEXT = "最多上传50个文档";

interface ChildMethods {
  focusInput: () => void;
  uploadFile: (v: File) => void;
}

export type QuestionInputUploadOptions = {
  accept: string;
  acceptImage: string;
  maxFileSize: number;
  maxFileNumber: number;
  maxImageNumber: number;
  acceptTypes: string[];
};

const getUploadStatus = (
  v: TRichModalFileStatus,
  globalError: TRichModalFileStatus | null,
) => {
  if (globalError && v !== ERichModalFileParseStatus.success) {
    return EUploadStatus.error;
  }
  switch (v) {
    case ERichModalFileNormStatus.uploading:
    case ERichModalFileNormStatus.parsing:
    case ERichModalFileNormStatus.longParsing:
      return EUploadStatus.uploading;
    case ERichModalFileParseStatus.success:
      return EUploadStatus.completed;
    default:
      return EUploadStatus.error;
  }
};

const { TextArea } = Input;

const QuestionInput = (
  {
    visible,
    isFetching,
    uploadOptions,
    handleSendMessage,
    maxLength,
    isLogin,
    gotoLogin,
    validateFiles,
    fileSelection,
    totalToken,
    onDeleteFileSelection,
    observeBody,
    fullDisplay,
    setCurrentShortCommand,
  }: {
    fullDisplay?: boolean;
    visible: boolean;
    isLogin: boolean;
    gotoLogin: () => void;
    validateFiles?: (fileList: IRichModalFile[]) => string | undefined;
    uploadOptions: QuestionInputUploadOptions;
    isFetching: boolean;
    handleSendMessage: (
      text: string,
      fileList: UploadedFile[],
      fileQueryInfo?: ISelection,
      type?: EFileQueryType,
    ) => void;
    maxLength: number;
    fileSelection?: ISelection;
    totalToken: number;
    onDeleteFileSelection?: () => void;
    observeBody: boolean;
    setCurrentShortCommand?: (commandItem: IShortCommand | null) => void;
  },
  ref: Ref<ChildMethods>,
) => {
  const [inputValue, setInputValue] = useState<string>("");
  const fileUploadOptions: QuestionInputUploadOptions = uploadOptions;
  const [vm] = useState(
    () =>
      new Vm(
        fileUploadOptions || {
          maxFileSize: 10,
          maxFileNumber: 1,
        },
        {
          totalToken,
        },
      ),
  );
  const { t } = useTranslation();
  const [getFocus, setGetFocus] = useState<boolean>(false);
  const [customeValidateError, setCustomeValidateError] = useState<string>("");
  const textRef = useRef<any>(null);
  const message = useMessage();

  const canSendMessage =
    !vm.inValidate &&
    !customeValidateError &&
    (vm.hasFile ? true : inputValue.trim() !== "");

  useEffect(() => {
    vm.setFileUploadProps({
      totalToken,
    });
  }, [totalToken]);

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!visible) return;
    if (!uploadOptions || gotoLoginWhenUnlogin()) return;
    const files = event.dataTransfer.files;
    let fileList = [...files];
    fileList = fileList.filter(
      item =>
        isFileNameAccept(fileUploadOptions.accept, item?.name) ||
        isFileNameAccept(fileUploadOptions.acceptImage, item?.name),
    );
    resolveMixedFilesAndImages(fileList);
  };

  useEffect(() => {
    if (observeBody) {
      // @ts-ignore
      document.body.addEventListener("dragover", handleDragOver);
      // @ts-ignore
      document.body.addEventListener("drop", handleDrop);

      return () => {
        // @ts-ignore
        document.body.removeEventListener("dragover", handleDragOver);
        // @ts-ignore
        document.body.removeEventListener("drop", handleDrop);
      };
    }
  }, [handleDragOver, handleDrop]);

  const gotoLoginWhenUnlogin = () => {
    if (!isLogin) {
      gotoLogin();
      return true;
    }
    return false;
  };

  // 检测是否同时上传图片与链接
  const isContainImgAndLink = (uploadFiles: UploadedFile[]) => {
    const linkFiles = SessionFile.getLinksFromText(inputValue);
    const imgFiles = uploadFiles.filter(ele => isImageFileName(ele.file_name));
    return !!imgFiles.length && !!linkFiles.length;
  };

  const sendMessage = () => {
    if (gotoLoginWhenUnlogin() || !canSendMessage) {
      return;
    }

    const uploadFiles = vm.uploadFileManager.fileList
      .map(item => item.uploadInfo!)
      .sort(
        (a: UploadedFile, b: UploadedFile) =>
          Number(isFileNameAccept(fileUploadOptions.accept, a?.file_name)) -
          Number(isFileNameAccept(fileUploadOptions.accept, b?.file_name)),
      );

    if (isContainImgAndLink(uploadFiles)) {
      message.warning("暂不支持同时发送网址和图片");
      return;
    }

    handleSendMessage(
      inputValue,
      uploadFiles,
      fileSelection,
      EFileQueryType.query,
    );
    vm.setValidType(EValidType.all);
    setInputValue("");
    setCurrentShortCommand?.(null);
    vm.clearFiles();
  };

  const validateAndUploadFileList = async (files: File[]) => {
    let addedFiles = [];
    const max =
      vm.validType === EValidType.image
        ? MAX_UPLOAD_IMAGE_NUMBER
        : MAX_UPLOAD_FILE_NUMBER;
    const overMaxCount =
      files.length + vm.uploadFileManager.fileList.length > max;

    if (overMaxCount) {
      message.warning(
        vm.validType === EValidType.image
          ? IMAGE_MAX_NUM_TEXT
          : FILE_MAX_NUM_TEXT,
      );
      addedFiles = files.slice(0, max - vm.uploadFileManager.fileList.length);
    } else {
      addedFiles = files;
    }

    addedFiles.forEach(file => {
      if (isImage(file)) {
        sendCLK("image_upload_initiate", "图片上传_触发图片上传");
      } else {
        sendCLK("file_upload_initiate", "文件上传_触发文件上传");
      }
    });

    if (addedFiles.length) await vm.uploadFileManager.addFiles(addedFiles);

    addedFiles.forEach(file => {
      const upOb = vm.uploadFileManager.fileList.find(
        item => item.file === file,
      );
      if (upOb?.uploadInfo?.status === "success") {
        if (isImage(file)) {
          sendCLK("image_upload_success", "图片上传_上传成功");
        } else {
          sendCLK("file_upload_success", "文件上传_上传成功");
        }
      } else {
        if (isImage(file)) {
          sendCLK("image_upload_fail", "图片上传_上传失败");
        } else {
          sendCLK("file_upload_fail", "文件上传_上传失败");
        }
      }
    });
  };

  const handleFileSelect = (files: FileList) => {
    // 处理某些浏览器通过设置inputType无法限制文件类型的场景
    const fileList = [...files].filter(
      item =>
        isFileNameAccept(fileUploadOptions.accept, item?.name) ||
        isFileNameAccept(fileUploadOptions.acceptImage, item?.name),
    );
    if (!fileList || fileList.length === 0) {
      return;
    }
    validateAndUploadFileList(fileList);
  };

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setInputValue(e.target.value);
  };

  const onPressEnter = (event: any) => {
    if (event.keyCode == 13) {
      if (event.metaKey || event.shiftKey || event.ctrlKey) {
        event.preventDefault();
        const cursorPos = event.target.selectionStart;
        // const prevH = event.target.scrollHeight;

        setInputValue((pre: string) => {
          if (cursorPos === undefined) return `${pre}\n`;
          const beforeText = pre.slice(0, cursorPos);
          const afterText = pre.slice(cursorPos);
          return `${beforeText}\n${afterText}`;
        });
        setTimeout(() => {
          event.target.setSelectionRange(cursorPos + 1, cursorPos + 1);
          // event.target.scrollTop += event.target.scrollHeight - prevH;
        });
      } else {
        event.preventDefault();
        if (isFetching) return;
        sendMessage();
      }
    }
  };
  const onPaste = (event: ClipboardEvent) => {
    if (!uploadOptions) return;
    const items = event.clipboardData.items;
    const fileList = [];
    let isFile = false;
    // 遍历粘贴的内容
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // 如果是文件类型
      if (item.kind === "file") {
        isFile = true;
        const file = item.getAsFile();
        if (
          isFileNameAccept(fileUploadOptions.accept, file?.name) ||
          isFileNameAccept(fileUploadOptions.acceptImage, file?.name)
        ) {
          fileList.push(file);
        }
      }
    }
    if (fileList.length > 0) {
      // validateAndUploadFileList(fileList as File[]);
      resolveMixedFilesAndImages(fileList as File[]);
      event.preventDefault();
      event.stopPropagation();
    } else if (isFile) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleUpload = async () => {
    if (gotoLoginWhenUnlogin()) return;
    if (vm.validType === EValidType.image) {
      message.warning(IMAGE_FILE_INVALID_TEXT);
      return;
    }
    const fileList = await pureSelectFiles(uploadOptions!.accept, true);
    if (vm.validType === EValidType.all) {
      vm.setValidType(EValidType.file);
    }
    handleFileSelect(fileList);
  };

  const handleUploadImage = async () => {
    if (gotoLoginWhenUnlogin()) return;
    if (vm.validType === EValidType.file) {
      message.warning(IMAGE_FILE_INVALID_TEXT);
      return;
    }
    const fileList = await pureSelectFiles(uploadOptions!.acceptImage, true);
    if (vm.validType === EValidType.all) {
      vm.setValidType(EValidType.image);
    }
    handleFileSelect(fileList);
  };

  const resolveMixedFilesAndImages = (fileList: File[]) => {
    if (vm.validType === EValidType.image) {
      const hasFile0 = fileList.some(file => !isImageFileName(file.name));
      if (hasFile0) message.warning(IMAGE_FILE_INVALID_TEXT);
      fileList = fileList.filter(file => isImageFileName(file.name));
      if (fileList) validateAndUploadFileList(fileList as File[]);
    } else if (vm.validType === EValidType.file) {
      const hasImage1 = fileList.some(file => isImageFileName(file.name));
      if (hasImage1) message.warning(IMAGE_FILE_INVALID_TEXT);
      fileList = fileList.filter(file => !isImageFileName(file.name));
      if (fileList) validateAndUploadFileList(fileList as File[]);
    } else {
      const hasFile2 = fileList.some(file => !isImageFileName(file.name));
      const hasImage2 = fileList.some(file => isImageFileName(file.name));
      if (hasFile2 && hasImage2) {
        message.warning(IMAGE_FILE_INVALID_TEXT);
      }
      if (hasFile2 || hasImage2)
        vm.setValidType(hasFile2 ? EValidType.file : EValidType.image);
      if (hasFile2) {
        fileList = fileList.filter(file => !isImageFileName(file.name));
      }
      if (fileList) validateAndUploadFileList(fileList as File[]);
    }
  };

  useImperativeHandle(ref, () => ({
    focusInput: () => {
      textRef.current?.focus();
    },
    setInputValue: (v: string) => {
      setInputValue(v);
    },
    getInputValue: () => {
      return inputValue;
    },
    startUpload: handleUpload,
    uploadFile: (file: File) => {
      if (!uploadOptions) return;
      // validateAndUploadFileList([file]);
      resolveMixedFilesAndImages([file]);
    },
    clear: () => {
      setInputValue("");
      vm.clearFiles();
    },
  }));

  useEffect(() => {
    if (validateFiles) {
      const customValidate = validateFiles(vm.uploadFileManager.fileList);
      if (customValidate) {
        setCustomeValidateError(customValidate);
        message.error(customValidate);
      } else {
        setCustomeValidateError("");
      }
    }
  }, [vm.uploadFileManager.fileList]);

  return (
    <div
      // onDrop={handleDrop}
      style={{ display: visible ? "flex" : "none" }}
      className={cls(
        styles.questionInput,
        fullDisplay && styles.fullDisplay,
        getFocus && styles.focused,
      )}
    >
      {vm.allCompleted && vm.lastFileComprehend < 100 && (
        <div className={styles.tip}>
          <ExclamationCircleFilled className={styles.icon} />
          {vm.lastFileComprehend > 0
            ? t("sensechat-135", {
                lastFileComprehend: vm.lastFileComprehend,
              })
            : t("sensechat-134")}
        </div>
      )}

      <div className={styles.fileContainer}>
        {vm.uploadFileManager.fileList.map((item, index) => {
          return (
            <FileDisplay
              key={index}
              onDelete={() => vm.deleteFile(item)}
              className={styles.file}
              file={item.file}
              fileInfo={item.uploadInfo!}
              uploadStatus={
                item.validateInfo
                  ? EUploadStatus.error
                  : getUploadStatus(
                      parseStatus(item),
                      vm.uploadFileManager.globalError,
                    )
              }
              statusText={getFileStatusText(
                item,
                uploadOptions!.maxFileSize,
                vm.uploadFileManager.globalError,
              )}
            />
          );
        })}
      </div>
      {fileSelection && (
        <div className={styles.refContainer}>
          <RefText
            refMode
            text={fileSelection.text}
            onDelete={onDeleteFileSelection}
          />
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.operations}>
          {isLogin && (
            <div id="chat-file-folder-dom" className={styles.folderBox}></div>
          )}
          {uploadOptions && (
            <>
              <div className={styles.uploadBox}>
                <Tooltip
                  placement="top"
                  trigger={["hover", "click"]}
                  color="#242424"
                  overlayInnerStyle={{ width: "520px" }}
                  title={
                    <div className={styles.uploadTip}>
                      <div>小技巧：支持直接复制、粘贴文档到输入框</div>
                      <div>上传要求</div>
                      <div>
                        1、支持上传pdf、doc、docx、ppt、pptx、txt、epub、mobi等格式文档；
                      </div>
                      <div>
                        2、单次最多支持上传50个文档，每个文档不超过30M。
                      </div>
                      <div>
                        功能提示：仅pdf格式文档支持划线提问、截图等功能。
                      </div>
                    </div>
                  }
                >
                  <FileUploadIcon
                    className={cls(
                      styles.uploadIcon,
                      // vm.arriveMaxLen && styles.uploadDisable,
                    )}
                    onClick={handleUpload}
                  />
                </Tooltip>
              </div>
              <div className={styles.uploadBox}>
                <Tooltip
                  placement="top"
                  trigger={["hover", "click"]}
                  color="#242424"
                  overlayInnerStyle={{ width: "520px" }}
                  title={
                    <div className={styles.uploadTip}>
                      <div>小技巧：支持直接复制、粘贴图片到输入框</div>
                      <div>上传要求</div>
                      <div>1、支持上传png、jpg、jepg、webp等格式图片</div>
                      <div>2、单次最多支持上传4张图片</div>
                      <div>3、每张图片不超过10M，最大分辨率13000*13000</div>
                    </div>
                  }
                >
                  <UploadImageIcon
                    className={cls(
                      styles.uploadIcon,
                      // vm.arriveMaxLen && styles.uploadDisable,
                    )}
                    onClick={handleUploadImage}
                  />
                </Tooltip>
              </div>
            </>
          )}
        </div>

        <TextArea
          ref={textRef}
          autoFocus={isLogin}
          value={inputValue}
          onChange={onChange}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onPaste={onPaste}
          onPressEnter={onPressEnter}
          onBlur={() => setGetFocus(false)}
          onFocus={() => {
            if (gotoLoginWhenUnlogin()) {
              textRef.current.blur();
              return;
            }
            setGetFocus(true);
          }}
          placeholder={
            inputValue && inputValue.length > 1
              ? ""
              : isLogin
                ? (t("sensechat-79") as string)
                : "点击登录，开启与商量的畅聊" // "请输入你想商量的问题"
          }
          // autoSize={{ minRows: 1, maxRows: !inputValue ? 1 : 4 }}
          autoSize={{ minRows: fullDisplay ? 2 : 1, maxRows: 4 }}
          className={styles.textArea}
          maxLength={maxLength}
        />
        {/* <Button
          loading={isFetching}
          loadingIcon={<LoadingIcon width={0.6} height={0.6} />}
          fill="none"
          onClick={sendMessage}
          className={styles.RightBtn}
        > */}
        <div className={styles.RightBtn}>
          {isFetching ? (
            <LoadingIcon
              className={styles.RightBtnLoading}
              width={0.6}
              height={0.6}
            />
          ) : (
            <Tooltip
              placement="top"
              trigger="hover"
              color="#242424"
              title="发送"
            >
              <SendMsgIcon
                className={cls(
                  styles.RightBtnIcon,
                  canSendMessage && styles.RightBtnHighlight,
                )}
                onClick={sendMessage}
              />
            </Tooltip>
          )}
        </div>
        {/* </Button> */}
      </div>
      {message.contextHolder}
    </div>
  );
};

export default observer(forwardRef(QuestionInput));
